import React, { useState, useEffect } from 'react';
import { safeUrl } from '../lib/safeUrl';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Building2, MapPin, DollarSign, ExternalLink, 
  Bookmark, BookmarkCheck, X, Globe, Factory, 
  TrendingUp, ArrowLeft, Lock, Share2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

const stageColors = {
  concept: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  design: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  tender: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  execution: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  complete: 'bg-gray-500/10 text-gray-500 border-gray-500/30'
};

export default function SharedProjectPage() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const isRTL = language === 'ar';
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    fetchSharedProject();
  }, [shareToken, token]);

  const fetchSharedProject = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/shared/project/${shareToken}`,
        { headers }
      );
      setProject(response.data.project);
      setIsSaved(response.data.project.is_saved);
      setIsAuthenticated(response.data.is_authenticated);
    } catch (err) {
      console.error('Error fetching shared project:', err);
      setError(err.response?.data?.detail || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error(language === 'ar' ? 'يجب تسجيل الدخول لحفظ المشروع' : 'Please log in to save this project');
      navigate('/login');
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${BACKEND_URL}/api/admin/shared/save`,
        { item_type: 'project', item_id: project.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsSaved(true);
      toast.success(language === 'ar' ? 'تم حفظ المشروع بنجاح' : 'Project saved successfully');
    } catch (err) {
      console.error('Error saving project:', err);
      toast.error(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsave = async () => {
    try {
      setSaving(true);
      await axios.delete(
        `${BACKEND_URL}/api/admin/shared/unsave`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          data: { item_type: 'project', item_id: project.id }
        }
      );
      setIsSaved(false);
      toast.success(language === 'ar' ? 'تمت إزالة المشروع من المحفوظات' : 'Project removed from saved items');
    } catch (err) {
      console.error('Error removing saved project:', err);
      toast.error(err.response?.data?.detail || 'Failed to remove project');
    } finally {
      setSaving(false);
    }
  };

  const handleDisregard = () => {
    navigate('/dashboard/projects');
  };

  const formatValue = (value) => {
    if (!value) return null;
    const { min, max, currency } = value;
    if (!min && !max) return null;
    
    const formatNum = (num) => {
      if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
      if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
      if (num >= 1e3) return `${(num / 1e3).toFixed(0)}K`;
      return num.toString();
    };
    
    const curr = currency || 'USD';
    if (min && max) return `${curr} ${formatNum(min)} - ${formatNum(max)}`;
    if (min) return `${curr} ${formatNum(min)}+`;
    return `${curr} ${formatNum(max)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-32 mb-6" />
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-8 w-3/4 mb-4" />
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-4 w-1/3 mb-4" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {language === 'ar' ? 'المشروع غير متاح' : 'Project Not Available'}
            </h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard/projects')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'العودة للمشاريع' : 'Back to Projects'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <Badge variant="outline" className="text-purple-500 border-purple-500/30">
            <Share2 className="w-3 h-3 mr-1" />
            {language === 'ar' ? 'مشروع مشترك' : 'Shared Project'}
          </Badge>
          <Badge variant="outline" className="text-amber-500 border-amber-500/30">
            <Lock className="w-3 h-3 mr-1" />
            {language === 'ar' ? 'خاص' : 'Private'}
          </Badge>
        </div>

        {/* Project Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <CardTitle className="text-2xl mb-2">{project.canonical_name}</CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-3">
                  <span className="flex items-center gap-1">
                    {countryFlags[project.country]}
                    {countryNames[language][project.country] || project.country}
                  </span>
                  {project.city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {project.city}
                    </span>
                  )}
                  {project.sector && (
                    <span className="flex items-center gap-1">
                      <Factory className="w-3 h-3" />
                      {project.sector}
                    </span>
                  )}
                </CardDescription>
              </div>
              <Badge className={stageColors[project.stage] || stageColors.concept}>
                {project.stage?.charAt(0).toUpperCase() + project.stage?.slice(1)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Value Estimate */}
            {project.value_estimate && formatValue(project.value_estimate) && (
              <div className="flex items-center gap-2 text-lg font-medium text-emerald-600">
                <DollarSign className="w-5 h-5" />
                {formatValue(project.value_estimate)}
              </div>
            )}

            {/* Description */}
            {project.description && (
              <div className="prose prose-sm max-w-none">
                <p className="text-muted-foreground">{project.description}</p>
              </div>
            )}

            {/* Location URL */}
            {safeUrl(project.location_url) && (
              <Button variant="outline" size="sm" asChild>
                <a href={safeUrl(project.location_url)} target="_blank" rel="noopener noreferrer">
                  <MapPin className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'عرض على الخريطة' : 'View on Map'}
                  <ExternalLink className="w-3 h-3 ml-2" />
                </a>
              </Button>
            )}

            {/* Created By */}
            {project.created_by_name && (
              <p className="text-sm text-muted-foreground">
                {language === 'ar' ? 'تم الإنشاء بواسطة' : 'Created by'}: {project.created_by_name}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <p className="text-center text-muted-foreground mb-2 sm:mb-0">
                {language === 'ar' 
                  ? 'هل تريد حفظ هذا المشروع في قائمتك؟' 
                  : 'Would you like to save this project to your list?'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mt-4">
              {isSaved ? (
                <Button 
                  onClick={handleUnsave} 
                  variant="outline"
                  disabled={saving}
                  className="min-w-[160px]"
                >
                  <BookmarkCheck className="w-4 h-4 mr-2 text-emerald-500" />
                  {saving 
                    ? (language === 'ar' ? 'جاري الإزالة...' : 'Removing...') 
                    : (language === 'ar' ? 'محفوظ' : 'Saved')}
                </Button>
              ) : (
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="min-w-[160px] bg-emerald-600 hover:bg-emerald-700"
                >
                  <Bookmark className="w-4 h-4 mr-2" />
                  {saving 
                    ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') 
                    : (language === 'ar' ? 'حفظ في قائمتي' : 'Save to My Feed')}
                </Button>
              )}
              <Button 
                onClick={handleDisregard} 
                variant="outline"
                className="min-w-[160px]"
              >
                <X className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'تجاهل' : 'Disregard'}
              </Button>
            </div>
            {!isAuthenticated && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                <Button variant="link" onClick={() => navigate('/login')} className="p-0 h-auto">
                  {language === 'ar' ? 'سجل الدخول' : 'Log in'}
                </Button>
                {' '}
                {language === 'ar' ? 'لحفظ هذا المشروع' : 'to save this project'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
